// Lib
import React, { useCallback, useEffect, useState } from 'react';
import Handsontable from 'handsontable/base';

// Utils
import { isLocationInbox, isLocationTrash } from '../../../../common/elements/utils/elementLocationUtils';
import { getTableScrollableElement } from '../utils/tableDOMUtils';
import {
    getElementId,
    getTableHorizontalScrollPosition,
    getTableHorizontalScrollWidth,
} from '../../../../common/elements/utils/elementPropertyUtils';
import { MNElement } from '../../../../common/elements/elementModelTypes';
import { TableColWidthsGU } from '../../../../common/table/TableTypes';
import TableOperations from './TableOperations';
import { isNil } from 'lodash';
import { IN_LIST_MODE } from '../../../../common/columns/columnConstants';

interface Props {
    element: MNElement;
    gridSize: number;
    tableContentColWidthsGU: TableColWidthsGU;
    isPresentational: boolean;
    isSingleSelected: boolean;
    isResizing: boolean;
    isDragging: boolean;
    shouldCoverTable: boolean;
    hotTableInstanceRef: React.MutableRefObject<Handsontable.Core | null>;
    hotTableContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    tableOperationsRef: React.RefObject<TableOperations>;
    getContextZoomScale: () => number;
    // TODO: Make args type more specific
    dispatchUpdateTableElement: (args: object) => void;
    inList: string | null;
}

const TableScrollHandlers = (props: Props) => {
    const {
        element,
        dispatchUpdateTableElement,
        isSingleSelected,
        isResizing,
        isDragging,
        shouldCoverTable,
        hotTableInstanceRef,
        hotTableContainerRef,
        inList,
    } = props;

    const [showHotCover, setShowHotCover] = useState(shouldCoverTable);
    const elementId = getElementId(element);
    const horizontalScrollPosition = getTableHorizontalScrollPosition(element);
    const horizontalScrollWidth = getTableHorizontalScrollWidth(element);

    // Is table inside a column/trash/unsorted notes etc where width is constrained
    const inElementList = isLocationInbox(element) || isLocationTrash(element);

    const tableScrollableElement = getTableScrollableElement(hotTableContainerRef.current);

    useEffect(() => {
        setShowHotCover(shouldCoverTable);
    }, [shouldCoverTable]);

    useEffect(() => {
        // Set viewportColumnRenderingOffset to 100 JUST AFTER the table has rendered in list view
        // This is to prevent the table from rendering extra columns offscreen when it's first rendered in list view
        // But the correct number of columns will be loaded after the initial render, ready for dragging and scrolling

        // small number of non-visible columns to be rendered to the left of the viewport
        // using 'auto' causes an issue where a table with a small width (<85px) will only render the first column
        const minOffset = 3;

        setTimeout(() => {
            if (!hotTableInstanceRef.current) return;

            hotTableInstanceRef.current.updateSettings({
                viewportColumnRenderingOffset: inElementList ? 100 : minOffset,
            });
        }, 0);
    }, [inElementList]);

    useEffect(() => {
        // set table scroll position from element data if it was scrolled
        if (!hotTableContainerRef.current || !horizontalScrollPosition) return;

        getTableScrollableElement(hotTableContainerRef.current)?.scrollTo(horizontalScrollPosition, 0);
    }, []);

    useEffect(() => {
        if (!tableScrollableElement || !dispatchUpdateTableElement || isResizing) return;

        // Persist scroll data to element state
        const canScroll = tableScrollableElement.scrollWidth > tableScrollableElement.clientWidth;

        const newHorizontalScrollPosition = canScroll ? tableScrollableElement.scrollLeft : null;
        const newHorizontalScrollWidth = tableScrollableElement.scrollWidth;

        if (newHorizontalScrollPosition !== horizontalScrollPosition) {
            dispatchUpdateTableElement({
                id: elementId,
                changes: {
                    horizontalScrollPosition: newHorizontalScrollPosition,
                },
                transactionId: -1,
            });
        }

        if (newHorizontalScrollWidth !== horizontalScrollWidth) {
            dispatchUpdateTableElement({
                id: elementId,
                changes: {
                    horizontalScrollWidth: newHorizontalScrollWidth,
                },
                transactionId: -1,
            });
        }
    }, [elementId, inElementList, isSingleSelected, tableScrollableElement?.clientWidth, isResizing]);

    // This function temporarily removes the table cover to allow scrolling
    // when the user is trying to scroll a table, and prevents horizontal canvas scrolling

    let scrollTimer: NodeJS.Timeout | null = null;

    const updateTableCover = useCallback(() => {
        // We should only run this function if the table can scroll, and the user is trying to scroll it
        const insideScrollableList = inElementList || inList === IN_LIST_MODE.IN_LIST_DOCUMENT_MODE;
        if (isSingleSelected || isNil(horizontalScrollPosition) || !insideScrollableList || isDragging) return;

        const canvasViewport = document.querySelector('.CanvasViewport');

        if (showHotCover) {
            // Prevent canvas scrolling
            canvasViewport?.classList.add('prevent-horizontal-scrolling');
            // Remove cover to allow scrolling
            setShowHotCover(false);
        }

        // If there is an existing timer, clear it
        if (scrollTimer !== null) clearTimeout(scrollTimer);

        // Set a timer to cover the table and reset the canvas scroll after a short delay
        scrollTimer = setTimeout(() => {
            canvasViewport?.classList.remove('prevent-horizontal-scrolling');
            setShowHotCover(true);
        }, 150);
    }, [isSingleSelected, horizontalScrollPosition, inElementList, isDragging, showHotCover]);

    return (
        <>
            {showHotCover && (
                // If element not selected, add cover over table in order to
                // prevent editing and allow dragging
                <div className="HotTableCover" onWheel={updateTableCover} onTouchMove={updateTableCover}></div>
            )}
        </>
    );
};

export default TableScrollHandlers;
